// What the Messages page says and how it orders things, kept apart from the drawing so
// each rule can be read and tested on its own: the one mixed list, the last line of a
// row, the times, and how a fresh page of messages joins the ones already on screen.
import type { ChatRow, GroupRow, Message } from '../../services/messagesApi';

/** Employee codes are compared the way the server keeps them: trimmed, lower case. */
export const sameCode = (a: unknown, b: unknown): boolean => {
  const x = String(a ?? '').trim().toLowerCase();
  return x !== '' && x === String(b ?? '').trim().toLowerCase();
};

/** Two letters for the round picture, as everywhere else on the web. */
export const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

/** The count on a row and on the top-bar link. */
export const unreadLabel = (n: number): string => (n > 99 ? '99+' : String(n));

export const peopleLabel = (n: number): string => (n === 1 ? '1 person' : `${n} people`);

// ── The list ───────────────────────────────────────────────────────────────────

/** One side of the list as it was loaded: its rows, and where its next page starts (null: no more). */
export interface ListSide<T> {
  rows: T[];
  next: number | null;
}

/** One row of the mixed list. */
export type ListRow =
  | { kind: 'direct'; key: string; id: string; seq: number; name: string; row: ChatRow }
  | { kind: 'group'; key: string; id: string; seq: number; name: string; row: GroupRow };

/**
 * One list out of the two the server keeps, newest first.
 *
 * `lastMessageSeq` is one counter across every conversation, so a single sort orders
 * chats and groups together. Each side is loaded a page at a time, and a side with
 * more to come is only known down to its last loaded row. So nothing older than that
 * is shown from EITHER side until "Load more" has fetched past it: otherwise an old
 * group would sit at the foot of the list above chats that are simply not loaded yet,
 * and move when they arrive. Rows only ever join at the foot.
 */
export function mixedList(chats: ListSide<ChatRow> | null, groups: ListSide<GroupRow> | null): ListRow[] {
  const knownDownTo = Math.max(chats?.next ?? -Infinity, groups?.next ?? -Infinity);
  const rows: ListRow[] = [
    ...(chats?.rows ?? []).map((row): ListRow => ({
      kind: 'direct', key: `c:${row.conversationId}`, id: row.conversationId, seq: row.lastMessageSeq,
      name: row.other?.name || row.other?.empCode || '', row,
    })),
    ...(groups?.rows ?? []).map((row): ListRow => ({
      kind: 'group', key: `g:${row.groupId}`, id: row.groupId, seq: row.lastMessageSeq, name: row.title, row,
    })),
  ];
  // Array.sort is stable, so two rows on the same number keep the order they came in.
  return rows.filter((r) => r.seq >= knownDownTo).sort((a, b) => b.seq - a.seq);
}

/**
 * A fresh first page laid over what is already loaded.
 *
 * The fresh page is the truth for everything at or above its last row: a row there
 * that the fresh page does not have is gone (a group the person left, a chat they hid
 * on the phone). Rows loaded earlier from further down are kept as they were, with
 * the place their next page starts.
 */
export function refreshSide<T>(
  held: ListSide<T>,
  fresh: ListSide<T>,
  idOf: (row: T) => string,
  seqOf: (row: T) => number,
): ListSide<T> {
  // No next page: the fresh page is the whole list.
  if (fresh.next === null) return fresh;
  const floor = fresh.next;
  const inFresh = new Set(fresh.rows.map(idOf));
  const older = held.rows.filter((row) => seqOf(row) < floor && !inFresh.has(idOf(row)));
  return { rows: [...fresh.rows, ...older], next: older.length > 0 ? held.next : fresh.next };
}

/** The next page added under what is loaded. A row that moved while paging is not listed twice. */
export function addOlderRows<T>(held: ListSide<T>, page: ListSide<T>, idOf: (row: T) => string): ListSide<T> {
  const have = new Set(held.rows.map(idOf));
  return { rows: [...held.rows, ...page.rows.filter((row) => !have.has(idOf(row)))], next: page.next };
}

// The server labels a photo or a voice note that has no words, so the row is never
// blank. Its labels carry an emoji and are the app's; the web says the same in words.
const MEDIA_LABELS: Array<[string, string]> = [
  ['📷 Photo', 'Photo'],
  ['🎤 Voice message', 'Voice note'],
];

const plainSnippet = (snippet: string | null): string => {
  const text = (snippet ?? '').trim();
  for (const [theirs, ours] of MEDIA_LABELS) {
    if (text.startsWith(theirs)) return `${ours}${text.slice(theirs.length)}`;
  }
  return text;
};

/**
 * The line under the name: the last message, with who said it.
 *
 * In a one-to-one chat it is "You: …" for the person's own and bare for the other
 * person's. "Own" is "not sent by the other person", which the server's row says by
 * itself, so it does not depend on the web knowing the right employee code. In a group
 * it is "You: …", "Name: …", or bare for a line the server wrote ("A added B").
 */
export function rowLine(item: ListRow, me: string): string {
  const text = plainSnippet(item.row.lastMessageSnippet);
  if (!text) return '';
  if (item.kind === 'direct') {
    const sender = item.row.lastMessageSender;
    return sender && !sameCode(sender, item.row.other?.empCode) ? `You: ${text}` : text;
  }
  const sender = item.row.lastMessageSender;
  if (!sender) return text;
  return sameCode(sender.empCode, me) ? `You: ${text}` : `${sender.name || sender.empCode}: ${text}`;
}

// ── Times ──────────────────────────────────────────────────────────────────────

// India time, like every other time of day on the web.
const IST = 'Asia/Kolkata';
const dayOf = (ms: number): string => new Date(ms).toLocaleDateString('en-CA', { timeZone: IST });
const clock = (ms: number): string =>
  new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: IST });
const date = (ms: number, now: number): string => {
  const sameYear = dayOf(ms).slice(0, 4) === dayOf(now).slice(0, 4);
  return new Date(ms).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: IST,
  });
};

/** A row's time: the time of day for today, "Yesterday", otherwise the date. Empty when there is none. */
export function rowTime(ms: number | null | undefined, now: number = Date.now()): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  if (dayOf(ms) === dayOf(now)) return clock(ms);
  // India has no clock change, so a day back is always 24 hours back.
  if (dayOf(ms) === dayOf(now - 24 * 60 * 60 * 1000)) return 'Yesterday';
  return date(ms, now);
}

/** A message's time: the time of day for today, the date and the time of day before that. */
export function messageTime(ms: number | null | undefined, now: number = Date.now()): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  return dayOf(ms) === dayOf(now) ? clock(ms) : `${date(ms, now)}, ${clock(ms)}`;
}

/** A voice note's length as m:ss, as the sending phone reported it. Empty when it did not. */
export function voiceLength(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '';
  const secs = Math.round(ms / 1000);
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

// ── The open conversation ──────────────────────────────────────────────────────

/** A message on screen, with the moment its photo or voice link was received. */
export interface HeldMessage extends Message {
  linkedAt: number;
}

// The server signs a photo or voice link for six hours and signs a new one on every
// read. A message already on screen keeps the link it has for five: taking the new one
// each time would change the address of every picture every few seconds, and the
// browser would fetch them all again. After five hours the new link is taken, so a
// voice note in a tab left open all day still plays. A message that no read brings
// back any more cannot take a new one: see mergeNewest.
const MEDIA_LINK_KEPT_MS = 5 * 60 * 60 * 1000;

const oldestFirst = (a: Message, b: Message): number => a.seq - b.seq;

const hold = (message: Message, now: number, was?: HeldMessage): HeldMessage => {
  const keepLink = !!was && !!was.media && !!message.media && !message.removed
    && was.media.kind === message.media.kind && now - was.linkedAt < MEDIA_LINK_KEPT_MS;
  return keepLink ? { ...message, media: was.media, linkedAt: was.linkedAt } : { ...message, linkedAt: now };
};

/** The first page of a conversation, turned oldest first for the screen. */
export const firstMessages = (page: Message[], now: number = Date.now()): HeldMessage[] =>
  page.map((m) => hold(m, now)).sort(oldestFirst);

/**
 * The newest page, read again, joined to what is on screen.
 *
 * A message already there is replaced by its fresh copy (so one that was removed
 * meanwhile loses its words), new ones are added, and older pages the person loaded
 * stay. Two cases start over, with this page alone. `replaced` says so, and the caller
 * then takes this page's own "earlier" position.
 *
 * One: a full page that shares nothing with what is on screen. More was sent in between
 * than one page holds, and neither has it, so keeping both would leave a hole in the
 * conversation that nothing marks.
 *
 * Two: a message above this page (loaded with "Load earlier", or pushed out of the
 * newest page since) has a photo or voice link past the time it is kept for. Only this
 * page comes back with new links, so that one would stop opening an hour later, in a
 * tab left open all day. Starting over takes it off the screen; "Load earlier" brings it
 * back with a link that works. Only a full page does this: the one message just sent is
 * joined on through here too, and it is not a page to start over from.
 */
export function mergeNewest(
  held: HeldMessage[],
  page: Message[],
  pageIsFull: boolean,
  now: number = Date.now(),
): { messages: HeldMessage[]; replaced: boolean } {
  const was = new Map(held.map((m) => [m.id, m]));
  if (pageIsFull && held.length > 0 && !page.some((m) => was.has(m.id))) {
    return { messages: firstMessages(page, now), replaced: true };
  }
  const fresh = new Map(page.map((m) => [m.id, hold(m, now, was.get(m.id))]));
  const kept = held.filter((m) => !fresh.has(m.id));
  if (pageIsFull && kept.some((m) => !!m.media && now - m.linkedAt >= MEDIA_LINK_KEPT_MS)) {
    // The pictures in this page keep the links they have, so they are not fetched again.
    return { messages: [...fresh.values()].sort(oldestFirst), replaced: true };
  }
  return { messages: [...kept, ...fresh.values()].sort(oldestFirst), replaced: false };
}

/** An older page added above what is on screen. */
export function addEarlier(held: HeldMessage[], page: Message[], now: number = Date.now()): HeldMessage[] {
  const have = new Set(held.map((m) => m.id));
  return [...page.filter((m) => !have.has(m.id)).map((m) => hold(m, now)), ...held].sort(oldestFirst);
}

/** The highest message number on screen, or 0. Reading is told to the server as "up to this number". */
export const newestSeq = (messages: Message[]): number =>
  messages.reduce((top, m) => (m.seq > top ? m.seq : top), 0);
